import { ChangeDetectorRef, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Routes } from '@angular/router';
import { SpendingAccountMainComponent } from './spending-main/spending-account-main.component';
import { SpendingAccountConsentsComponent } from './spending-account-consents/spending-account-consents.component';
import { SpendingAccountIntroComponent } from './spending-account-intro/spending-account-intro.component';
import { SpendingAccountCreateComponent } from './spending-account-create/spending-account-create.component';
import { SpendingAccountVerificationComponent } from './spending-account-verification/spending-account-verification.component';
import {
  DocumentsKeyEnum,
  LegalDocumentsService,
  RequiredLegalDocumentsComponent,
  SharedLegalDocumentsModule,
  HasLegalDocsGuard,
} from '@brightside-web/shared/legal-documents';
import { SharedDesktopModule } from '@brightside-web/shared/desktop';

import {
  AccountCreationSteps,
  FinancialAccountCreationErrorComponent,
  FinancialAccountCreationGuard,
  FinancialAccountsCoreService,
  FinancialAccountsCreationService,
  FinancialAccountsHistoryService,
  FinancialAccountTypes,
  FinancialCoreKYCComponent,
  FinancialCoreKYCVerificationInProgressComponent,
} from '@brightside-web/desktop/feature/financial-accounts-core';

import { HasSpendingAccountGuard } from './has-spending-account-guard.service';
import { NewSpendingAccountService } from './new-spending-account.service';
import { BrightsideUiModule } from '@brightside/brightside-ui';
import { TranslateModule } from '@ngx-translate/core';
import { SharedAccountModule } from '@brightside-web/shared/account';
import { MicroUiMoleculeModule } from '@micro-ui/molecule';
import { FeatureFlagKillSwitchGuard } from '@brightside-web/desktop/data-access/core-services';
import { KnownFeatureFlags } from '@brightside-web/desktop/data-access/shared';
import { SpendingAccountTransactionHistoryComponent } from './spending-account-transaction-history/spending-account-transaction-history.component';
import { SpendingAccountStatementsComponent } from './spending-account-statements/spending-account-statements.component';
import { ReactiveFormsModule } from '@angular/forms';
import { DebitCardService } from './debit-card/debit-card.service';
import { CashAdvanceService } from './cash-advance/cash-advance.service';
import { MarkdownModule } from 'ngx-markdown';

const productType = FinancialAccountTypes.SPENDING;

const ROUTES: Routes = [
  {
    path: '',
    component: SpendingAccountMainComponent,
    children: [
      {
        path: 'creation',
        canActivate: [HasSpendingAccountGuard],
        children: [
          {
            path: AccountCreationSteps.INTRO,
            component: SpendingAccountIntroComponent,
            canActivate: [FinancialAccountCreationGuard],
            data: { creationStep: AccountCreationSteps.INTRO },
          },
          {
            path: AccountCreationSteps.VERIFICATION,
            component: SpendingAccountVerificationComponent,
            canActivate: [FinancialAccountCreationGuard],
            data: { creationStep: AccountCreationSteps.VERIFICATION },
          },
          {
            path: AccountCreationSteps.CONSENTS,
            component: SpendingAccountConsentsComponent,
            canActivate: [FinancialAccountCreationGuard],
            data: { creationStep: AccountCreationSteps.CONSENTS },
          },
          {
            path: AccountCreationSteps.CREATE,
            component: SpendingAccountCreateComponent,
            canActivate: [FinancialAccountCreationGuard],
            data: { creationStep: AccountCreationSteps.CREATE },
          },
          {
            path: AccountCreationSteps.KYC,
            component: FinancialCoreKYCComponent,
            canActivate: [FinancialAccountCreationGuard],
            data: { accountType: 'spending', creationStep: AccountCreationSteps.CREATE },
          },
          {
            path: AccountCreationSteps.KYC_VERIFICATION,
            component: FinancialCoreKYCVerificationInProgressComponent,
            data: { accountType: 'spending' },
          },
          {
            path: 'error',
            component: FinancialAccountCreationErrorComponent,
          },
          {
            path: '**',
            redirectTo: AccountCreationSteps.INTRO,
          },
        ],
      },
      {
        path: 'transactions',
        component: SpendingAccountTransactionHistoryComponent,
        canActivate: [HasSpendingAccountGuard],
      },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('@brightside-web/desktop-v3/feature/spending-dashboard').then(
            (m) => m.SpendingDashboardPageComponent
          ),
        canActivate: [HasSpendingAccountGuard, HasLegalDocsGuard],
        data: {
          pageConfig: {
            documentsKey: DocumentsKeyEnum.SPENDING_ACCOUNT,
            legalRoute: ['accounts', 'spending', 'legal'],
          },
        },
      },
      {
        path: 'legal',
        component: RequiredLegalDocumentsComponent,
        canActivate: [HasSpendingAccountGuard],
        data: {
          pageConfig: {
            documentsKey: DocumentsKeyEnum.SPENDING_ACCOUNT,
            pageOnForwardCtaPath: ['accounts', 'spending', 'dashboard'],
            showCloseButton: true,
            showBackbutton: false,
            closeButtonRoute: ['home'],
          },
        },
      },
      {
        path: 'statements',
        component: SpendingAccountStatementsComponent,
        canActivate: [HasSpendingAccountGuard],
      },
      {
        path: 'digital-card-details',
        loadChildren: () => import('@brightside-web/micro/digital-card-details').then((m) => m.MicroDigitalCardDetailsModule),
        canLoad: [FeatureFlagKillSwitchGuard],
        canActivateChild: [FeatureFlagKillSwitchGuard],
        data: {
          displayBackButton: true,
          featureFlagInfo: {
            flag: [KnownFeatureFlags.DIGITALCARDACCESS],
            toOnFalse: ['accounts/spending'],
          },
        },
      },
      {
        path: 'debit-card',
        loadChildren: () => import('./debit-card/debit-card.module').then((m) => m.DebitCardModule),
        canLoad: [FeatureFlagKillSwitchGuard],
        canActivateChild: [FeatureFlagKillSwitchGuard],
        data: {
          featureFlagInfo: {
            flag: KnownFeatureFlags.DIGITALCARDACCESS,
            toOnFalse: ['accounts/spending'],
          },
        },
      },
      {
        path: 'cash-advance',
        loadChildren: () => import('./cash-advance/cash-advance.module').then((m) => m.CashAdvancedModule),
      },
      {
        path: '**',
        redirectTo: 'dashboard',
      },
    ],
  },
];

@NgModule({
  imports: [
    CommonModule,
    RouterModule.forChild(ROUTES),
    SharedDesktopModule,
    SharedLegalDocumentsModule,
    BrightsideUiModule,
    TranslateModule,
    SharedAccountModule,
    MicroUiMoleculeModule,
    ReactiveFormsModule,
    MarkdownModule.forChild(),
  ],
  declarations: [
    SpendingAccountMainComponent,
    SpendingAccountConsentsComponent,
    SpendingAccountIntroComponent,
    SpendingAccountCreateComponent,
    SpendingAccountVerificationComponent,
    SpendingAccountTransactionHistoryComponent,
    SpendingAccountStatementsComponent,
  ],
  providers: [
    FinancialAccountsCreationService,
    FinancialAccountsCoreService,
    FinancialAccountsHistoryService,
    HasSpendingAccountGuard,
    FinancialAccountCreationGuard,
    LegalDocumentsService,
    HasLegalDocsGuard,
    DebitCardService,
    CashAdvanceService,
  ],
})
export class DesktopFeatureSpendingAccountModule {}
